import React, { useState, useRef, useCallback } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import { Button } from '@/ui';
import { Editor } from '@tinymce/tinymce-react';
import { Property, isDropdownOrMultiSelect } from '../../../views/CustomProperties/Properties/types';
import ColorPicker from '../../ColorPicker/ColorPicker';
import ZuulToggleSwitch from '../../ZuulToggleSwitch/ZuulToggleSwitch';
import { useDebouncyFn } from 'use-debouncy';
import { MediaLibraryModal } from '../../ZuulMediaLibrary/MediaLibraryModal';
import { MediaItem } from '../../ZuulMediaLibrary';
import { PropertyValue } from '../CustomProperties/types.ts';
import { ZuulEditor } from '../../ZuulEditor/ZuulEditor';
import { createRoot } from 'react-dom/client';
import { cfg } from '../../../runtime-config';
import { env } from '../../../../config';

interface PropertyFieldProps {
  property: Property;
  value: PropertyValue | undefined;
  setValue: (propertyKey: string, value: PropertyValue) => void;
  fieldKey: string;
  propertyKey: string;
  isDisplayOnly?: boolean;
}

interface PropertyFieldComponent extends React.FC<PropertyFieldProps> {
  SingleLine: React.FC<PropertyFieldProps>;
  MultiSelect: React.FC<PropertyFieldProps>;
  Boolean: React.FC<PropertyFieldProps>;
  ColorProperty: React.FC<PropertyFieldProps>;
  MultiLineText: React.FC<PropertyFieldProps>;
  WYSIWYG: React.FC<PropertyFieldProps>;
  Radio: React.FC<PropertyFieldProps>;
  Dropdown: React.FC<PropertyFieldProps>;
  Image: React.FC<PropertyFieldProps>;
}

/** Autocomplete needs an array; a property's configured default may be anything. */
const toStringList = (value: unknown): string[] => (Array.isArray(value) ? (value as string[]) : []);

const FieldRow: React.FC<{
  property: Property;
  propertyKey: string;
  fieldKey: string;
  children: React.ReactNode;
}> = ({ property, propertyKey, fieldKey, children }) => (
  <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
    <Grid size={{ xs: 12, md: 4 }}>
      <PropertyLabel property={property} propertyKey={propertyKey} fieldKey={fieldKey} />
      {property.description && (
        <Typography variant='caption' component='div' sx={{ color: 'text.secondary' }}>
          {property.description}
        </Typography>
      )}
    </Grid>
    <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
      {children}
    </Grid>
  </Grid>
);

const PropertyFieldBase: React.FC<PropertyFieldProps> = (props: PropertyFieldProps) => {
  switch (props.property.type) {
    case 'Single line Text':
      return <SingleLine {...props} />;
    case 'Multi-line Text':
      return <MultiLineText {...props} />;
    case 'WYSIWYG':
      return <WYSIWYG {...props} />;
    case 'Multi-select':
      return <MultiSelect {...props} />;
    case 'Dropdown':
      return <Dropdown {...props} />;
    case 'Radio':
      return <Radio {...props} />;
    case 'Boolean':
      return <Boolean {...props} />;
    case 'Color':
      return <ColorProperty {...props} />;
    case 'Image':
      return <Image {...props} />;
    default:
      return <>Invalid Property Type</>;
  }
};

function SingleLine({ property, value, setValue, propertyKey, fieldKey, isDisplayOnly }: PropertyFieldProps) {
  return (
    <Box sx={{ width: '100%' }}>
      <FieldRow property={property} propertyKey={propertyKey} fieldKey={fieldKey}>
        <TextField
          id={fieldKey || property.replacementKey}
          value={(value as string) ?? (property.additionalConfiguration?.defaultValue as string) ?? ''}
          onChange={(e) => setValue(propertyKey, e.target.value)}
          disabled={!!isDisplayOnly}
        />
      </FieldRow>
    </Box>
  );
}

function MultiSelect({ property, value, setValue, propertyKey, fieldKey, isDisplayOnly }: PropertyFieldProps) {
  return (
    <Box sx={{ width: '100%' }}>
      <FieldRow property={property} propertyKey={propertyKey} fieldKey={fieldKey}>
        <Autocomplete
          multiple
          fullWidth
          id={propertyKey}
          options={(isDropdownOrMultiSelect(property) && property.additionalConfiguration?.fieldValues) || []}
          // A default that is not a list would make Autocomplete an uncontrolled component.
          value={toStringList(value ?? property.additionalConfiguration?.defaultValue)}
          onChange={(_event, next) => setValue(propertyKey, next)}
          disabled={!!isDisplayOnly || false}
          renderInput={(params) => <TextField {...params} placeholder='Select options' />}
        />
      </FieldRow>
    </Box>
  );
}

function Boolean({ property, value, setValue, propertyKey, fieldKey, isDisplayOnly }: PropertyFieldProps) {
  return (
    <Box sx={{ width: '100%' }}>
      <FieldRow property={property} propertyKey={propertyKey} fieldKey={fieldKey}>
        <ZuulToggleSwitch
          value={(value ?? property.additionalConfiguration?.defaultValue ?? false) as boolean}
          onChange={(value) => setValue(propertyKey, value)}
          labelComponent={<></>}
          disabled={!!isDisplayOnly || false}
        ></ZuulToggleSwitch>
      </FieldRow>
    </Box>
  );
}

function ColorProperty({ property, value, setValue, fieldKey, propertyKey, isDisplayOnly }: PropertyFieldProps) {
  const setColor = useDebouncyFn((color: string) => setValue(propertyKey, color), 200);
  return (
    <Box sx={{ width: '100%' }}>
      <FieldRow property={property} propertyKey={propertyKey} fieldKey={fieldKey}>
        <ColorPicker
          fieldKey={fieldKey}
          color={value || property.additionalConfiguration?.defaultValue || ''}
          setColor={setColor}
          disabled={!!isDisplayOnly || false}
        />
      </FieldRow>
    </Box>
  );
}

function MultiLineText({ property, value, setValue, propertyKey, fieldKey, isDisplayOnly }: PropertyFieldProps) {
  return (
    <Box sx={{ width: '100%' }}>
      <FieldRow property={property} propertyKey={propertyKey} fieldKey={fieldKey}>
        <TextField
          multiline
          minRows={3}
          id={fieldKey || propertyKey}
          value={(value as string) ?? (property.additionalConfiguration?.defaultValue as string) ?? ''}
          onChange={(e) => setValue(propertyKey, e.target.value)}
          disabled={!!isDisplayOnly}
        />
      </FieldRow>
    </Box>
  );
}

function WYSIWYG({ property, value, setValue, propertyKey, fieldKey, isDisplayOnly }: PropertyFieldProps) {
  const contentRef = useRef<string>(
    (value as string) || (property.additionalConfiguration?.defaultValue as string) || ''
  );

  const url =
    import.meta.env.MODE === 'development' || (cfg('LOCAL_TESTING') && cfg('LOCAL_TESTING') === 'true')
      ? 'http://localhost:3000/'
      : env.baseURL || '/';

  const zuulContentRef = useRef((value as string) || (property.additionalConfiguration?.defaultValue as string) || '');

  const handleChange = useCallback((content: string) => {
    contentRef.current = content;
    zuulContentRef.current = content;
  }, []);

  const handleBlur = useCallback(() => {
    setValue(propertyKey, contentRef.current);
  }, [propertyKey, setValue]);

  return (
    <Box sx={{ width: '100%' }}>
      <FieldRow property={property} propertyKey={propertyKey} fieldKey={fieldKey}>
        <Editor
          key={fieldKey || propertyKey}
          initialValue={(value as string) || (property.additionalConfiguration?.defaultValue as string) || ''}
          tinymceScriptSrc={url + 'tinymce/tinymce.min.js'}
          licenseKey='gpl'
          // `readonly`, not `disabled`: tinymce-react 6 maps `disabled` to TinyMCE's newer `disabled`
          // option, which our TinyMCE 7.5 lacks, and its fallback leaves the toolbar enabled.
          // `readonly` reaches init as `readonly: true`, as `disabled` did in tinymce-react 4.
          readonly={!!isDisplayOnly}
          init={{
            width: '100%',
            height: 400,
            resize: 'both',
            plugins: [
              'advlist',
              'autolink',
              'lists',
              'emoticons',
              'link',
              'image',
              'charmap',
              'anchor',
              'searchreplace',
              'visualblocks',
              'fullscreen',
              'insertdatetime',
              'table',
              'preview',
              'help',
            ],
            toolbar:
              'undo redo | blocks | bold italic | alignleft aligncenter alignright alignjustify | outdent indent | showCodeView',
            promotion: false,
            branding: false,
            toolbar_mode: 'sliding',
            skin: 'oxide-dark',
            forced_root_block: 'div',
            newline_behaviour: 'linebreak',
            external_valid_elements: 'script[src|async|defer|type|charset]',
            valid_children: '+div[style], +div[script]',
            schema: 'html5',
            menubar: true, // Hides the menu bar for a more compact look
            protect: [/<script>[\s\S]*?<\/script>/g, /<style>[\s\S]*?<\/style>/g],
            setup: (ed) => {
              let container: any = null;
              ed.ui.registry.addView('code', {
                onShow: (api) => {
                  container = api.getContainer();
                  container.__zuulRoot = createRoot(container);
                  container.__zuulRoot.render(
                    <ZuulEditor
                      content={zuulContentRef.current}
                      handleEditorChange={(content: string) => {
                        zuulContentRef.current = content;
                      }}
                      height='330px'
                      language='html'
                      validate={true}
                      readOnly={false}
                    />
                  );
                },
                onHide: () => {
                  if (container.__zuulRoot) {
                    container.__zuulRoot.unmount();
                    delete container.__zuulRoot;
                  }
                },
                buttons: [
                  {
                    type: 'button',
                    text: 'Cancel',
                    buttonType: 'secondary',
                    onAction: () => {
                      handleChange(ed.getContent());
                      ed.execCommand('ToggleView', false, 'code');
                    },
                  },
                  {
                    type: 'button',
                    text: 'Save code',
                    buttonType: 'primary',
                    onAction: () => {
                      handleChange(zuulContentRef.current);
                      ed.execCommand('ToggleView', false, 'code');
                    },
                  },
                ],
              });

              ed.ui.registry.addButton('showCodeView', {
                icon: 'sourcecode',
                text: '',
                onAction: (_api) => {
                  ed.execCommand('ToggleView', false, 'code');
                },
              });
            },
          }}
          onEditorChange={handleChange}
          onBlur={handleBlur}
          onDrop={(e) => e.stopPropagation()}
        />
      </FieldRow>
    </Box>
  );
}

function Dropdown({ property, value, setValue, propertyKey, fieldKey, isDisplayOnly }: PropertyFieldProps) {
  return (
    <Box sx={{ width: '100%' }}>
      <FieldRow property={property} propertyKey={propertyKey} fieldKey={fieldKey}>
        <Autocomplete
          fullWidth
          id={propertyKey}
          options={(isDropdownOrMultiSelect(property) && property.additionalConfiguration?.fieldValues) || []}
          value={(value as string) || (property.additionalConfiguration?.defaultValue as string) || null}
          onChange={(_event, next) => setValue(propertyKey, next ?? '')}
          disabled={!!isDisplayOnly || false}
          renderInput={(params) => <TextField {...params} placeholder='Select an option' />}
        />
      </FieldRow>
    </Box>
  );
}

function Radio({ property, value, setValue, propertyKey, fieldKey, isDisplayOnly }: PropertyFieldProps) {
  const fieldValues = (isDropdownOrMultiSelect(property) && property.additionalConfiguration?.fieldValues) || [];
  const icons: Record<string, string> = (property.additionalConfiguration as any)?.icons || {};
  const current = (value as string) || (property.additionalConfiguration?.defaultValue as string) || '';

  return (
    <Box sx={{ width: '100%' }}>
      <FieldRow property={property} propertyKey={propertyKey} fieldKey={fieldKey}>
        <ToggleButtonGroup
          exclusive
          color='primary'
          value={current}
          onChange={(_event, next) => next !== null && setValue(propertyKey, next)}
          disabled={!!isDisplayOnly}
        >
          {fieldValues.map((opt: string) => {
            const hasIcon = icons?.[opt];

            return (
              <ToggleButton key={opt} value={opt}>
                {/* The icon is a Bootstrap Icons class held in the property's own configuration,
                    so it cannot be swapped for an MUI glyph here. */}
                {hasIcon ? <i className={`bi ${icons[opt]}`} style={{ fontSize: '1.2em' }} /> : opt}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </FieldRow>
    </Box>
  );
}

function Image({ property, value, setValue, propertyKey, isDisplayOnly, fieldKey }: PropertyFieldProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const mediaItemValue = (value as { id: string; altText: string; mediaItem: MediaItem | null } | undefined) ||
    property.additionalConfiguration?.defaultValue || { id: '', altText: '', mediaItem: null };

  const updateImageValue = (updates: Partial<{ id: string; altText: string; mediaItem: MediaItem | null }>) => {
    const newValue = { ...mediaItemValue, ...updates };
    setValue(propertyKey, newValue);
  };

  const handleMediaSelect = (item: MediaItem) => {
    updateImageValue({ mediaItem: item });
  };

  const clearSelection = () => {
    updateImageValue({ mediaItem: null });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <FieldRow property={property} propertyKey={propertyKey} fieldKey={fieldKey}>
        <Card sx={{ minHeight: 200, position: 'relative', width: '100%' }}>
          <CardHeader
            sx={{ pb: 1 }}
            title={
              <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
                <Button
                  color='success'
                  size='sm'
                  onClick={() => setIsModalOpen(true)}
                  disabled={!!isDisplayOnly}
                  startIcon={<ImageOutlined />}
                >
                  Select Media Item
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {mediaItemValue.mediaItem && !isDisplayOnly && (
                  <Button color='danger' size='sm' onClick={clearSelection} title='Clear selection'>
                    <DeleteOutlined fontSize='small' />
                  </Button>
                )}
              </Stack>
            }
            slotProps={{ title: { component: 'div' } }}
          />
          <CardContent>
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Stack spacing={1.5}>
                  <div>
                    <FormLabel htmlFor={`${propertyKey}-id`} sx={{ display: 'block', mb: 0.5, fontSize: '0.9em' }}>
                      ID (optional):
                    </FormLabel>
                    <TextField
                      id={`${propertyKey}-id`}
                      value={mediaItemValue.id}
                      onChange={(e) => updateImageValue({ id: e.target.value })}
                      placeholder='Element ID'
                      disabled={!!isDisplayOnly}
                    />
                  </div>
                  <div>
                    <FormLabel htmlFor={`${propertyKey}-alt`} sx={{ display: 'block', mb: 0.5, fontSize: '0.9em' }}>
                      Alt Text (optional):
                    </FormLabel>
                    <TextField
                      id={`${propertyKey}-alt`}
                      value={mediaItemValue.altText}
                      onChange={(e) => updateImageValue({ altText: e.target.value })}
                      placeholder='Media item alt text'
                      disabled={!!isDisplayOnly}
                    />
                  </div>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                {mediaItemValue.mediaItem ? (
                  <CardMedia
                    component='img'
                    src={mediaItemValue.mediaItem.url}
                    alt={mediaItemValue.mediaItem.fileName}
                    sx={{ objectFit: 'contain', border: 1, borderColor: 'divider', borderRadius: 1 }}
                  />
                ) : (
                  <Stack
                    direction='row'
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                      width: '100%',
                      minHeight: 120,
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      fontSize: '0.9em',
                    }}
                  >
                    <ImageOutlined sx={{ fontSize: '2em' }} />
                    <span>No media item selected</span>
                  </Stack>
                )}
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <Box sx={{ px: 2, py: 1.5 }}>
            {mediaItemValue.mediaItem ? mediaItemValue.mediaItem.fileName : 'No File Selected'}
          </Box>
        </Card>
        <MediaLibraryModal
          isOpen={isModalOpen}
          toggle={() => setIsModalOpen(false)}
          onSelect={handleMediaSelect}
          selectedItem={mediaItemValue.mediaItem}
          isSelectionMode={true}
        />
      </FieldRow>
    </Box>
  );
}

const PropertyLabel = ({
  property,
  propertyKey,
  fieldKey,
}: {
  property: Property;
  propertyKey: string;
  fieldKey: string;
}) => {
  // Sanitize fieldKey to ensure valid HTML ID
  const sanitizedFieldKey = fieldKey.replace(/[^a-zA-Z0-9_-]/g, '_');
  return (
    <>
      <FormLabel id={`label-${sanitizedFieldKey}`} htmlFor={sanitizedFieldKey}>
        {property.label}
      </FormLabel>
    </>
  );
};

const MemoizedPropertyFieldBase = React.memo(
  PropertyFieldBase,
  (prev, next) =>
    prev.property === next.property &&
    prev.propertyKey === next.propertyKey &&
    prev.fieldKey === next.fieldKey &&
    prev.isDisplayOnly === next.isDisplayOnly &&
    prev.value === next.value
);

export default Object.assign(MemoizedPropertyFieldBase, {
  SingleLine,
  MultiSelect,
  Boolean,
  ColorProperty,
  MultiLineText,
  WYSIWYG,
  Radio,
  Dropdown,
  Image,
}) as PropertyFieldComponent;
